import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date for display
 * @param date - Date to format (Date object or string in YYYY-MM-DD format)
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
): string {
  let dateObj: Date;

  if (typeof date === 'string') {
    // Check if it's an ISO 8601 format (includes T for time)
    if (date.includes('T')) {
      // ISO format with time - use standard parsing, it handles UTC correctly
      dateObj = new Date(date);
    } else {
      // Simple YYYY-MM-DD format - parse manually to avoid timezone issues
      // Dates in format "YYYY-MM-DD" are parsed as UTC, which can shift the day
      const parts = date.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts.map(Number);
        dateObj = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        // Fallback to standard parsing if not in expected format
        dateObj = new Date(date);
      }
    }
  } else {
    dateObj = date;
  }

  // Validate the date is valid
  if (isNaN(dateObj.getTime())) {
    console.error('Invalid date:', date);
    return 'Unknown date';
  }

  return new Intl.DateTimeFormat('en-US', options).format(dateObj);
}

/**
 * Format a category name by removing underscores and converting to title case
 * @param category - Category string to format (e.g., "character_state")
 * @returns Formatted category string (e.g., "Character State")
 */
export function formatCategory(category: string): string {
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
